// SettingsSidebar.tsx
import { Settings, X } from "lucide-react";

export interface SettingsTab {
    id: string;
    label: string;
    icon: React.ReactNode;
}

export function SettingsSidebar({
    tabs,
    activeTab,
    onTabChange,
    mobileOpen = false,
    onMobileClose,
}: {
    tabs: SettingsTab[];
    activeTab: string;
    onTabChange: (tabId: string) => void;
    mobileOpen?: boolean;
    onMobileClose?: () => void;
}) {
    const handleSelect = (tabId: string) => {
        onTabChange(tabId);
        if (onMobileClose) {
            onMobileClose();
        }
    };

    const sidebarContent = (isMobile: boolean = false) => (
        <div className="flex flex-col h-full w-full bg-white dark:bg-gray-900 transition-colors">
            {/* Header */}
            <div className="h-12 w-full shrink-0 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between px-3 bg-gray-50/50 dark:bg-gray-900/50">
                <div className="flex items-center gap-3">
                    <div className="bg-orange-600 p-1.5 rounded-lg border border-orange-500/30 shrink-0">
                        <Settings className="text-white h-4 w-4" />
                    </div>
                    <h1 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide whitespace-nowrap">
                        Settings
                    </h1>
                </div>
                {isMobile && onMobileClose && (
                    <button
                        onClick={onMobileClose}
                        className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                        aria-label="Close settings drawer"
                    >
                        <X size={18} />
                    </button>
                )}
            </div>

            {/* Tab List */}
            <div className="flex-1 overflow-y-auto custom-scroll p-1.5 space-y-1">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => handleSelect(tab.id)}
                        className={`w-full flex items-center gap-3 px-3 h-9 rounded-md transition-all duration-200 border cursor-pointer ${activeTab === tab.id
                            ? "bg-orange-600 text-white shadow-sm border-orange-500/20"
                            : "text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 border-transparent"
                            }`}
                    >
                        <span className="shrink-0 flex items-center justify-center w-5 h-5">
                            {tab.icon}
                        </span>
                        <span className="font-bold text-[11px] uppercase tracking-wider whitespace-nowrap">
                            {tab.label}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <>
            {/* Desktop Sidebar (hidden on mobile, visible on md and up) */}
            <div className="hidden md:flex flex-col h-full w-54 shrink-0 border-r border-gray-200 dark:border-gray-800 transition-colors">
                {sidebarContent(false)}
            </div>

            {/* Mobile Drawer (Slide-over from left) */}
            {mobileOpen && (
                <div className="fixed inset-0 z-50 md:hidden flex">
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-xs transition-opacity animate-in fade-in duration-200"
                        onClick={onMobileClose}
                        aria-hidden="true"
                    />

                    {/* Drawer Content */}
                    <div className="relative w-64 max-w-[80vw] h-full shadow-2xl border-r border-gray-200 dark:border-gray-800 z-10 flex flex-col animate-in slide-in-from-left duration-200">
                        {sidebarContent(true)}
                    </div>
                </div>
            )}
        </>
    );
}
